import { FC, ReactNode, useState } from 'react';
import { Form, FormikHelpers, useFormikContext } from 'formik';
import { useTranslation } from 'react-i18next';
import { TFunction } from 'i18next';
import * as Yup from 'yup';
import { FormikDebounce } from '../common/form/FormikDebounce';
import { FormContainer } from '../common/form/FormContainer';
import { FormGroup } from '../common/form/FormGroup';
import { QcsButton } from '../common/basic/QcsButton';
import { DetailHeader } from '../common/DetailHeader';
import { ShowError } from '../ShowError';
import { ShowResourceContent } from '../ShowResourceContent';
import { QcsMinimalistStepper } from '../invitation/QcsMinimalistStepper';
import { InvitationButtonsContainer } from '../invitation/InvitationButtonsContainer';
import {
  IntegrationContactFields,
  integrationFormValidation,
  IntegrationFormValues,
  IntegrationGeneralFields,
  IntegrationSubmitButton,
  toApiClientUpdateDto,
  toIntegrationFormValues,
} from './IntegrationFormFields';
import { IntegrationIpRanges } from './IntegrationIpRanges';
import { IntegrationKeyFields } from './IntegrationKeyFields';
import { IntegrationAuthMethodField } from './IntegrationAuthMethodField';
import {
  API_ACCESS_PATH,
  getKeyFormInitialValues,
  keyFormValidation,
  KeyFormValues,
  showMissingRequirementError,
  toApiClientKeyCreateDto,
} from './integrationUtils';
import { apiClientService } from '@api/services/services';
import { resources } from '@api/resources';
import { ApiScopeDto } from '@api/models/apiClient/ApiScopeDto';
import { ApiClientDetailDto } from '@api/models/apiClient/ApiClientDetailDto';
import { IntegrationScopeMatrix } from './IntegrationScopeMatrix';

type CreateFormValues = IntegrationFormValues & KeyFormValues;

const getCreateValidation = (t: TFunction, scopeCatalogue: ApiScopeDto[]) => {
  const integrationValidation = integrationFormValidation(t, scopeCatalogue);

  return {
    ...integrationValidation,
    ...keyFormValidation(t),
    //A key that never expires has no reminders, so the hidden contact isn't checked.
    contactEmail: integrationValidation.contactEmail.when('keyNeverExpires', { is: true, then: () => Yup.string() }),
  };
};

//A key that never expires has no reminders - nobody to remind.
const CreateContactFields: FC = () => {
  const { values } = useFormikContext<CreateFormValues>();
  return values.keyNeverExpires ? null : <IntegrationContactFields />;
};

interface CreateStep {
  title: string;
  //Only these fields are validated before moving on, so a step never reports errors of the steps ahead.
  fields: (keyof ReturnType<typeof getCreateValidation>)[];
  content: ReactNode;
}

interface Props {
  scopeCatalogue: ApiScopeDto[];
  onCreated: (detail: ApiClientDetailDto) => void;
}

export const IntegrationCreate: FC<Props> = ({ scopeCatalogue, onCreated }) => {
  const { t } = useTranslation();
  const [stepIndex, setStepIndex] = useState(0);

  const steps: CreateStep[] = [
    {
      title: t('integration.apiAccess.basicDetails'),
      fields: ['name', 'scopes'],
      content: (
        <>
          <IntegrationGeneralFields />
          <FormGroup name="integration.apiAccess.scopes.title">
            <IntegrationScopeMatrix catalogue={scopeCatalogue} />
          </FormGroup>
        </>
      ),
    },
    {
      title: t('integration.apiAccess.security'),
      fields: ['ipRanges'],
      content: (
        <>
          <FormGroup name="integration.apiAccess.authMethod.title">
            <IntegrationAuthMethodField />
          </FormGroup>
          <FormGroup name="integration.apiAccess.ipRanges.title">
            <IntegrationIpRanges />
          </FormGroup>
        </>
      ),
    },
    {
      title: t('integration.apiAccess.key.title'),
      fields: ['publicKeyPem', 'keyExpiresAt', 'contactEmail'],
      content: (
        <>
          <FormGroup>
            <IntegrationKeyFields />
          </FormGroup>
          <CreateContactFields />
        </>
      ),
    },
  ];

  const step = steps[stepIndex];
  const isLastStep = stepIndex === steps.length - 1;

  //Resetting to the current values starts the other step clean - no errors, touched fields or submit count carried over.
  const goToStep = (index: number, values: CreateFormValues, resetForm: FormikHelpers<CreateFormValues>['resetForm']) => {
    resetForm({ values });
    setStepIndex(index);
  };

  const handleSubmit = async (values: CreateFormValues, { resetForm }: FormikHelpers<CreateFormValues>) => {
    if (!isLastStep) {
      goToStep(stepIndex + 1, values, resetForm);
      return;
    }

    const res = await apiClientService
      .create({
        ...toApiClientUpdateDto({ ...values, notifyContact: values.notifyContact && !values.keyNeverExpires }),
        authMethod: values.authMethod,
        key: toApiClientKeyCreateDto(values),
      })
      .catch((error) => {
        showMissingRequirementError(t, error, resources.createApiClient, scopeCatalogue);
        throw error;
      });

    onCreated(res.data);
  };

  return (
    <>
      <DetailHeader title="integration.apiAccess.createTitle" backText="common.cancel" backTo={API_ACCESS_PATH} />
      <QcsMinimalistStepper stepNumber={stepIndex + 1} totalSteps={steps.length} stepTitle={step.title} />
      <ShowResourceContent resource={resources.apiClientScopes}>
        <FormikDebounce<CreateFormValues>
          initialValues={{ ...toIntegrationFormValues(), ...getKeyFormInitialValues() }}
          validationSchema={Yup.object(getCreateValidation(t, scopeCatalogue)).pick(step.fields)}
          onSubmit={handleSubmit}
        >
          {({ values, resetForm }) => (
            <Form>
              <FormContainer>
                {step.content}
                {isLastStep && <ShowError resource={resources.createApiClient} />}
                <InvitationButtonsContainer>
                  {stepIndex > 0 ? (
                    <QcsButton variant="outlined" onClick={() => goToStep(stepIndex - 1, values, resetForm)}>
                      {t('common.back')}
                    </QcsButton>
                  ) : (
                    <div />
                  )}
                  <div style={{ width: '100%' }} />
                  <IntegrationSubmitButton scopeCatalogue={scopeCatalogue} text={isLastStep ? 'integration.apiAccess.create' : 'common.nextStep'} />
                </InvitationButtonsContainer>
              </FormContainer>
            </Form>
          )}
        </FormikDebounce>
      </ShowResourceContent>
    </>
  );
};
